// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductsStickyFooter from "@bg/App/Components/OnePageCheckout/ProductsStickyFooter";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { xClassNames } from "@bg/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef } from "react";
import { css } from "styled-components";

type Props = WidgetProps;

export interface OnePageCheckoutPageContainerStyles {
    container?: GridContainerProps;
    leftGridItem?: GridItemProps;
    spaceGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    // XNG-Change
    wrapper?: InjectableCss;
    backToCartLink?: LinkPresentationProps;
    leftSection?: InjectableCss;
    title?: TypographyPresentationProps;
    detailsContainer?: InjectableCss;
    addressesContainer?: InjectableCss;
    shippingContainer?: InjectableCss;
    paymentContainer?: InjectableCss;
    productsContainer?: InjectableCss;
    rightSection?: InjectableCss;
    stickyHeaderWrapper?: InjectableCss;
    // TODO: Enhancement when refactor the cartline
    productsContainerTablet?: InjectableCss;
}

export const containerStyles: OnePageCheckoutPageContainerStyles = {
    leftGridItem: {
        width: [12, 12, 6, 7, 7],
        css: css`
            & > div > *:not(:last-child) {
                padding-bottom: 30px;
                margin-bottom: 30px;
                border-bottom: 1px solid ${getColor("common.border")};
            }
        `,
    },
    spaceGridItem: { width: [0, 0, 1, 1, 1] },
    rightGridItem: {
        width: [12, 12, 5, 4, 4],
        css: css`
            & > div > *:not(:last-child) {
                margin-bottom: 10px;
            }
        `,
    },
};

const styles = containerStyles;

/**
 * XNG-Changes
 * Add return to cart button
 * Change structure
 */
const OnePageCheckoutPageContainer = ({ id }: Props) => {
    const [stickyVisible, setStickyVisible] = React.useState(false);
    const productsListRef = useRef<HTMLDivElement | null>(null);
    const summarySetionRef = useRef<HTMLDivElement | null>(null);

    const { cartUrl, isMobileScreen } = useAppSelector(state => ({
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        isMobileScreen: (state as CustomAppState).commonActions["isMobileScreen"],
    }));

    useEffect(() => {
        const handleSticky = () => {
            const productsListVisible = productsListRef.current?.getBoundingClientRect().top;
            const summarySectionVisible = summarySetionRef.current?.getBoundingClientRect().top;

            if (summarySectionVisible && window.innerHeight > summarySectionVisible) {
                setStickyVisible(false);
            } else if (productsListVisible && window.innerHeight > productsListVisible) {
                setStickyVisible(true);
            } else {
                setStickyVisible(false);
            }
        };
        if (isMobileScreen) {
            document.addEventListener("scroll", handleSticky, { passive: true });
            handleSticky();
        }
        return () => {
            document.removeEventListener("scroll", handleSticky);
        };
    }, [isMobileScreen]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link href={cartUrl} {...styles.backToCartLink}>
                {translate("Return to Cart")}
            </Link>
            <Typography {...styles.title}>{translate("Checkout")}</Typography>
            <StyledWrapper {...styles.container} data-test-selectors="CheckoutPageContainer">
                <StyledWrapper {...styles.leftSection}>
                    <StyledWrapper {...styles.detailsContainer}>
                        <Zone zoneName="Content0" contentId={id} normalWidth={true} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.addressesContainer}>
                        <Zone zoneName="Content3" contentId={id} normalWidth={true} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.shippingContainer}>
                        <Zone zoneName="Content4" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.paymentContainer}>
                        <Zone zoneName="Content5" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productsContainer}>
                        <Zone zoneName="Content6" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    {...styles.rightSection}
                    ref={summarySetionRef}
                    id="onePageCheckout_SummarySectionContainer"
                >
                    <Zone zoneName="Content1" contentId={id} />
                </StyledWrapper>
                <StyledWrapper {...styles.productsContainerTablet} ref={productsListRef}>
                    <Zone zoneName="Content6" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper
                className={xClassNames.append([stickyVisible ? "stickyVisible" : "stickyHidden"])}
                data-test-selector="productDetails-sticky-header"
                {...styles.stickyHeaderWrapper}
            >
                <ProductsStickyFooter />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutPageContainer,
    definition: {
        displayName: "Page Container",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
